import React, { useMemo, memo } from "react";
import styles from "./analisiDrawdownBacktestTable.module.scss";
import { useTranslation } from "react-i18next";
import { formatItalianNumber } from "utils/formatUtils";

type AnalisiDrawdownBacktestProps = {
  data: any;
  title: string;
};

const AnalisiDrawdownBacktestComponent: React.FC<
  AnalisiDrawdownBacktestProps
> = ({ data, title = "ciao ne" }) => {
  const { t } = useTranslation();

  const dataAnalisiDrawdownMax =
    data.analisi_backtest.analisi_drawdown_max_drawdown_stats;
  const dataAnalisiDrawdownRecovery =
    data.analisi_backtest.analisi_drawdown_recovery_time_stats;
  const staticData = [
    [
      {
        type: "labelTitle",
        label: t("portfolio.analisiDrawdown"),
      },
      {
        type: "label",
        label: t("portfolio.maximumDrawdown"),
      },
      {
        type: "label",
        label: t("portfolio.recoveryPeriod"),
      },
    ],
    [
      {
        type: "labelTitle",
        label: t("portfolio.ptf"),
      },
      {
        type: "value",
        value: dataAnalisiDrawdownMax.find(
          (v) => v.label === "Maximum Drawdown",
        )?.weight,
      },
      {
        type: "value",
        value: dataAnalisiDrawdownRecovery.find(
          (v) => v.label === "Recovery period (mesi)",
        )?.weight,
      },
    ],
    [
      {
        type: "labelTitle",
        label: t("portfolio.inizio"),
      },
      {
        type: "value",
        value: dataAnalisiDrawdownMax.find((v) => v.label === "Inizio")?.weight,
      },
      {
        type: "value",
        value: dataAnalisiDrawdownRecovery.find((v) => v.label === "Inizio")
          ?.weight,
      },
    ],
    [
      {
        type: "labelTitle",
        label: t("portfolio.fine"),
      },
      {
        type: "value",
        value: dataAnalisiDrawdownMax.find((v) => v.label === "Fine")?.weight,
      },
      {
        type: "value",
        value: dataAnalisiDrawdownRecovery.find((v) => v.label === "Fine")
          ?.weight,
      },
    ],
    [
      {
        type: "labelTitle",
        label: t("portfolio.durataMesi"),
      },
      {
        type: "value",
        value: dataAnalisiDrawdownMax.find((v) => v.label === "Durata (mesi)")
          ?.weight,
      },
      {
        type: "value",
        value: dataAnalisiDrawdownRecovery.find(
          (v) => v.label === "Durata (mesi)",
        )?.weight,
      },
    ],
  ];

  const columns = 5;
  const totalRows = 3;
  const rows = [];

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row = rowIndex + 1;

    rows.push(row);
  }
  const cells = useMemo(() => {
    const generatedCells = [];
    for (const row of rows) {
      for (let colIndex = 0; colIndex < columns; colIndex++) {
        const cell = {
          row,
          col: colIndex + 1,
        };

        generatedCells.push(cell);
      }
    }

    return generatedCells;
  }, [rows, columns]);
  const dashValue = (dataValue: any): any => {
    if (!dataValue || dataValue === undefined) {
      return "-";
    }
    if (typeof dataValue === "string") {
      return dataValue;
    }
    return formatItalianNumber(dataValue);
  };

  const dashValueData = (dataValue: any): any => {
    if (!dataValue || dataValue === undefined) {
      return "-";
    }
    if (typeof dataValue === "string") {
      const [year, month, day] = dataValue.split("-");
      const formattedDate = `${day}/${month}/${year}`;
      return formattedDate;
    }
    return formatItalianNumber(dataValue);
  };

  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;
      const dato = staticData[col - 1][row - 1];
      if (dato.type === "labelTitle") {
        return (
          <div
            key={`${row}-${col}`}
            className={[
              col === 1 ? styles.titleCellLeft : styles.titleCellRight,
              styles.productName,
            ].join(" ")}
          >
            {dato.label}
          </div>
        );
      }
      if (dato.type === "label") {
        return (
          <div
            key={`${row}-${col}`}
            className={[
              styles.cellKeys,
              row === 2 ? styles.cellBorder : "",
            ].join(" ")}
          >
            {dato.label}
          </div>
        );
      }
      if (dato.type === "value") {
        if (row >= 2 && col > 2 && col < 5) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                styles.cellValue,
                row === 2 ? styles.cellBorder : "",
              ].join(" ")}
            >
              {dashValueData(dato.value)}
            </div>
          );
        }
        return (
          <div
            key={`${row}-${col}`}
            className={[
              styles.cellValue,
              row === 2 ? styles.cellBorder : "",
            ].join(" ")}
          >
            {dashValue(dato.value)}
          </div>
        );
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, data]);

  return (
    <div>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
          {renderedCells}
        </div>
      </div>
    </div>
  );
};

export const AnalisiDrawdownBacktest = memo(
  AnalisiDrawdownBacktestComponent,
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      prevProps.title === nextProps.title
    );
  },
);

export default AnalisiDrawdownBacktest;
